"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Heart, Home, Calendar, Crown, Sparkles, Users, Coffee } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LenormandPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="mb-4 p-3 bg-pink-500/10 border border-pink-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-pink-300">
                <Heart className="h-4 w-4" />
                <span className="text-sm font-medium">🃏 Tarot</span>
                <span className="text-pink-500">/</span>
                <span className="text-sm">Sistemler</span>
                <span className="text-pink-500">/</span>
                <span className="text-sm">Lenormand Kartları</span>
              </div>
            </div>

            <div className="mb-6">
              <Link href="/about" className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors">
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link href="/about/tarot/systems" className="text-slate-400 hover:text-slate-200 transition-colors">
                Tarot Sistemleri
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Lenormand Kartları</span>
            </div>

            <div className="bg-gradient-to-r from-pink-900/20 to-rose-900/20 rounded-xl p-8 mb-8 border border-pink-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Heart className="h-10 w-10 text-pink-400" />
                Lenormand Kartları
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                36 kartlık pratik divination sistemi. Marie Anne Lenormand'dan ismini alır.
                Günlük hayat odaklı, direct ve somut yorumlama yaklaşımı sunar.
              </p>
            </div>

            {/* Deck Structure Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Deste Yapısı</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="bg-gradient-to-br from-emerald-500 to-green-700 rounded-xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                        <Sparkles className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white">36 Lenormand Kartı</h3>
                        <p className="text-emerald-100 text-sm">Marie Anne Lenormand'ın fortune telling geleneğinden gelen 36 sembolik kart</p>
                      </div>
                    </div>
                    <Link
                      href="/about/tarot/systems/lenormand/cards"
                      className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      Kartları İncele
                      <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
                    </Link>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white/10 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Coffee className="h-4 w-4 text-emerald-200" />
                        <span className="text-sm font-medium text-white">Günlük Semboller</span>
                      </div>
                      <p className="text-xs text-emerald-100">Ev, gemi, köpek gibi tanıdık objeler</p>
                    </div>

                    <div className="bg-white/10 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Home className="h-4 w-4 text-emerald-200" />
                        <span className="text-sm font-medium text-white">Direct Anlamlar</span>
                      </div>
                      <p className="text-xs text-emerald-100">Anlaşılır ve somut yorumlar</p>
                    </div>

                    <div className="bg-white/10 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="h-4 w-4 text-emerald-200" />
                        <span className="text-sm font-medium text-white">Kombinasyon Okuma</span>
                      </div>
                      <p className="text-xs text-emerald-100">Kartlar birlikte anlam kazanır</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marie Anne Lenormand</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                      <Crown className="h-5 w-5 text-pink-400" />
                      Hayatı (1772-1843)
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Fransız falcı ve kart okuyucusu</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Napolyon döneminin ünlü medyumu</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Aristokrasi ve siyasiler arasında popüler</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-pink-400" />
                      Divination Stili
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Normal oyun kartları kullanıyordu</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Praktik ve somut öngörüler</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Günlük hayat odaklı yorumlar</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">36 Kart Sistemi</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  { num: 1, name: "Binici", icon: "🐎" },
                  { num: 2, name: "Yonca", icon: "🍀" },
                  { num: 3, name: "Gemi", icon: "🚢" },
                  { num: 4, name: "Ev", icon: "🏠" },
                  { num: 5, name: "Ağaç", icon: "🌳" },
                  { num: 6, name: "Bulutlar", icon: "☁️" },
                  { num: 7, name: "Yılan", icon: "🐍" },
                  { num: 8, name: "Tabut", icon: "⚰️" },
                  { num: 9, name: "Buket", icon: "💐" },
                  { num: 10, name: "Tırpan", icon: "🔄" },
                  { num: 11, name: "Kırbaç", icon: "💫" },
                  { num: 12, name: "Kuşlar", icon: "🐦" },
                  { num: 13, name: "Çocuk", icon: "👶" },
                  { num: 14, name: "Tilki", icon: "🦊" },
                  { num: 15, name: "Ayı", icon: "🐻" },
                  { num: 16, name: "Yıldızlar", icon: "⭐" },
                  { num: 17, name: "Leylek", icon: "🦢" },
                  { num: 18, name: "Köpek", icon: "🐕" },
                ].map((card) => (
                  <div key={card.num} className="bg-slate-800 rounded-lg p-4 border border-slate-700 text-center">
                    <div className="text-2xl mb-2">{card.icon}</div>
                    <div className="text-pink-400 font-semibold text-sm">{card.num}</div>
                    <div className="text-slate-300 text-xs">{card.name}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tarot'tan Farkları</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-700 rounded-lg flex items-center justify-center">
                      <Coffee className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Praktik Yaklaşım</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">36 kart (78 değil)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Günlük objeler ve durumlar</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Direct, literal yorumlar</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <Home className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Somut Odak</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Ev, iş, ilişkiler odaklı</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Spiritual-mistik değil, pratik</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Zaman çerçeveleri net</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Okuma Teknikleri</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Temel Yöntemler</h3>
                    <ul className="text-sm text-slate-400 space-y-2">
                      <li>• <strong>3'lü Kart:</strong> Geçmiş-Şimdi-Gelecek</li>
                      <li>• <strong>5'li Kart:</strong> Durum analizi</li>
                      <li>• <strong>9'lu Kare:</strong> Detaylı inceleme</li>
                      <li>• <strong>Grand Tableau:</strong> Tüm 36 kart</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Kart Kombinasyonları</h3>
                    <ul className="text-sm text-slate-400 space-y-2">
                      <li>• <strong>Ev + Kalp:</strong> Aile mutluluğu</li>
                      <li>• <strong>Gemi + Yıldızlar:</strong> Uzak yolculuk</li>
                      <li>• <strong>Tilki + Para:</strong> İş hilesi</li>
                      <li>• <strong>Köpek + Yüzük:</strong> Sadık arkadaşlık</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
              <p className="text-amber-100 leading-relaxed">
                Lenormand kartları sembolik yorumlama aracıdır ve kesinlik iddiası taşımaz. İçerikler tamamen
                merak, eğlence ve kişisel farkındalık amaçlıdır. Yaşamsal kararlar için uzman görüşü alınmalıdır.
              </p>
            </div>

            {/* Main Tarot Training CTA */}
            <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6 mb-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold text-slate-100 mb-3">🎴 Tarot Eğitimleri</h3>
                <p className="text-slate-300 mb-6 leading-relaxed max-w-2xl mx-auto">
                  Tarot kart sistemleri, sembolik yorumlama teknikleri, kart dizilimleri ve okuma yöntemleri hakkında
                  detaylı eğitim programları için kayıt olun. Merak ettiğiniz konularda rehberlik alın.
                </p>
                <button className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg">
                  <Users className="h-5 w-5 mr-2" />
                  Tarot Eğitimi İçin Kayıt Ol
                </button>
                <p className="text-slate-400 text-sm mt-3">
                  * Eğitim programları sadece bilgilendirme ve kişisel gelişim amaçlıdır
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/tarot/systems/golden-dawn"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Golden Dawn Tarot
              </Link>

              <Link
                href="/about/tarot/systems/oracle"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Oracle Kartları
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
      </div>
    </AboutLayout>
  );
}